import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Globe,
  Github,
  Twitter,
  Loader,
  Server
} from 'lucide-react';
import axios from 'axios';
import { rateLimiter } from '../services/rateLimiter';
import { subnetNames } from '../utils/subnetNames';
import { subnetDescriptions } from '../utils/subnetDescriptions';
import { getTwitterUsers } from '../services/twitterService';
import { getTensorpulseMindshare, getTensorpulseMindshareHistory } from '../services/tensorpulseMindshareService';
import type { TwitterUser } from '../types/twitter';
import type { SubnetMindshare, SubnetMindshareHistory } from '../types/mindshare';
import type { SubnetPool } from '../types/subnet';
import { SubnetMindshareView } from '../components/subnet/SubnetMindshareView';

const filterDataByTimeRange = (
  data: SubnetMindshareHistory['history']
): SubnetMindshareHistory['history'] => {
  return data;
};

export default function SubnetPage() {
  const { netuid } = useParams<{ netuid: string }>();
  const navigate = useNavigate();
  const [subnetData, setSubnetData] = useState<SubnetPool | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [taoPrice, setTaoPrice] = useState<number | null>(null);
  const [twitterUser, setTwitterUser] = useState<TwitterUser | null>(null);
  const [loadingTwitter, setLoadingTwitter] = useState(false);
  const [mindshareData, setMindshareData] = useState<SubnetMindshare | null>(null);
  const [loadingMindshare, setLoadingMindshare] = useState(false);
  const [mindshareHistory, setMindshareHistory] = useState<SubnetMindshareHistory | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [isMindshareHistoryOpen, setIsMindshareHistoryOpen] = useState(false);
  const [mindsharePercentChange, setMindsharePercentChange] = useState<number>(0);
  const [hasFetchedHistory, setHasFetchedHistory] = useState(false);

  useEffect(() => {
    let mounted = true;
    const controller = new AbortController();

    const fetchData = async () => {
      if (!netuid) return;

      try {
        setLoading(true);
        setError(null);

        await rateLimiter.checkRateLimit();
        
        const taoResponse = await axios.get('https://api.taostats.io/api/price/latest/v1?asset=tao', {
          headers: {
            'accept': 'application/json',
            'Authorization': 'tao-1b237a3c-dcb4-468c-ad70-6b47e40e2552:6473c9a3'
          },
          signal: controller.signal
        });

        if (mounted && taoResponse.data?.data?.[0]?.price) {
          setTaoPrice(Number(taoResponse.data.data[0].price));
        }

        const response = await axios.get(
          'https://api.taostats.io/api/dtao/pool/latest/v1?page=1&limit=1&netuid=' + netuid,
          {
            headers: {
              'accept': 'application/json',
              'Authorization': 'tao-1b237a3c-dcb4-468c-ad70-6b47e40e2552:6473c9a3'
            },
            signal: controller.signal
          }
        );

        if (!mounted) return;

        if (!response.data?.data?.[0]) {
          throw new Error('No data found for this subnet');
        }

        setSubnetData(response.data.data[0]);
      } catch (err) {
        if (!mounted) return;
        
        if (axios.isCancel(err)) {
          return;
        }
        
        console.error('Failed to fetch subnet data:', err);
        setError('Failed to load subnet data');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [netuid]);

  useEffect(() => {
    if (!netuid) return;
    
    const subnetId = parseInt(netuid);
    const subnetInfo = subnetDescriptions[subnetId];
    
    if (!subnetInfo?.x) return;
    
    const fetchTwitterUser = async () => {
      try {
        setLoadingTwitter(true);
        
        const xUrlMatch = subnetInfo.x.match(/twitter\.com\/(\w+)|x\.com\/(\w+)/i);
        if (!xUrlMatch) return;
        
        const xUsername = (xUrlMatch[1] || xUrlMatch[2] || '').toLowerCase();
        
        const allUsers = await getTwitterUsers(100);
        const matchingUser = allUsers.find(user => 
          user.username.toLowerCase() === xUsername
        );
        
        if (matchingUser) {
          setTwitterUser(matchingUser);
        }
      } catch (err) {
        console.error('Failed to fetch Twitter user:', err);
      } finally {
        setLoadingTwitter(false);
      }
    };
    
    fetchTwitterUser();
  }, [netuid]);

  useEffect(() => {
    if (!netuid) return;
    
    const fetchMindshareData = async () => {
      try {
        setLoadingMindshare(true);
        const allMindshareData = await getTensorpulseMindshare();
        
        const subnetMindshare = allMindshareData.find(
          item => item.netuid === parseInt(netuid)
        );
        
        if (subnetMindshare) {
          setMindshareData(subnetMindshare);
        }
      } catch (err) {
        console.error('Failed to fetch mindshare data:', err);
      } finally {
        setLoadingMindshare(false);
      }
    };
    
    fetchMindshareData();
  }, [netuid]);

  useEffect(() => {
    if (!netuid || hasFetchedHistory) return;
    
    const fetchMindshareHistory = async () => {
      try {
        setLoadingHistory(true);
        console.log(`Fetching mindshare history for subnet ${netuid}`);
        const history = await getTensorpulseMindshareHistory(parseInt(netuid));
        
        if (history) {
          console.log(`Received history for subnet ${netuid} with ${history.history.length} entries`);
          const sortedHistory = [...history.history].sort(
            (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          );
          
          const filteredHistory = filterDataByTimeRange(sortedHistory);
          
          setMindshareHistory({
            ...history,
            history: filteredHistory
          });
          
          if (filteredHistory.length >= 2) {
            const oldestValue = filteredHistory[0].mindsharePercentage;
            const newestValue = filteredHistory[filteredHistory.length - 1].mindsharePercentage;
            
            if (oldestValue > 0) {
              const change = ((newestValue - oldestValue) / oldestValue) * 100;
              setMindsharePercentChange(change);
            }
          }
        } else {
          console.log(`No history found for subnet ${netuid}`);
        }
      } catch (err) {
        console.error('Failed to fetch mindshare history:', err);
      } finally {
        setLoadingHistory(false);
        setHasFetchedHistory(true);
      }
    };
    
    fetchMindshareHistory();
  }, [netuid, hasFetchedHistory]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-12 bg-black/20 rounded-lg"></div>
          <div className="flex gap-4">
            <div className="flex-1 h-[400px] bg-black/20 rounded-lg"></div>
            <div className="hidden md:grid grid-cols-2 gap-4 w-96">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-32 bg-black/20 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !subnetData || !netuid) {
    return (
      <div className="space-y-6">
        <div className="bg-red-900/50 border border-red-700 rounded-lg p-4 text-red-200">
          {error || 'Failed to load subnet data'}
        </div>
      </div>
    );
  }

  const subnetId = parseInt(netuid);
  const subnetInfo = subnetDescriptions[subnetId];

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-4">
            {loadingTwitter ? (
              <div className="w-16 h-16 rounded-full bg-black/20 flex items-center justify-center">
                <Loader className="w-6 h-6 text-cyan-400 animate-spin" />
              </div>
            ) : subnetId === 14 ? (
              <img 
                src="https://pbs.twimg.com/profile_images/1922656948530577409/mLnpdbFj_400x400.jpg" 
                alt={`${subnetNames[subnetId]} logo`}
                className="w-16 h-16 rounded-full border-2 border-cyan-400 object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://abs.twimg.com/sticky/default_profile_images/default_profile_400x400.png';
                }}
              />
            ) : twitterUser ? (
              <img 
                src={twitterUser.profile_image_url.replace('_normal', '')} 
                alt={`${subnetNames[subnetId]} logo`}
                className="w-16 h-16 rounded-full border-2 border-cyan-400 object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://abs.twimg.com/sticky/default_profile_images/default_profile_400x400.png';
                }}
              />
            ) : subnetInfo?.x ? (
              <div className="w-16 h-16 rounded-full bg-black/20 flex items-center justify-center">
                <Twitter className="w-8 h-8 text-cyan-400" />
              </div>
            ) : (
              <div className="w-16 h-16 rounded-full bg-black/20 flex items-center justify-center">
                <Server className="w-8 h-8 text-cyan-400" />
              </div>
            )}
            
            <div className="flex flex-col md:flex-row md:items-baseline gap-2 md:gap-4">
              <h1 className="text-2xl md:text-3xl font-bold">
                {subnetNames[subnetId] || 'Unknown'}
              </h1>
              <p className="text-sm md:text-base text-gray-400">
                Subnet {netuid} - Rank {subnetData.rank}
              </p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {subnetInfo?.website && (
            <a
              href={subnetInfo.website}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-black/20 hover:bg-black/30 rounded-lg text-sm font-medium transition-colors"
            >
              <Globe className="w-5 h-5" />
            </a>
          )}
          {subnetInfo?.github && (
            <a
              href={subnetInfo.github}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-black/20 hover:bg-black/30 rounded-lg text-sm font-medium transition-colors"
            >
              <Github className="w-5 h-5" />
            </a>
          )}
          {subnetInfo?.x && (
            <a
              href={subnetInfo.x}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-black/20 hover:bg-black/30 rounded-lg text-sm font-medium transition-colors"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                className="w-5 h-5"
              >
                <path d="M4 4l11.733 16h4.267l-11.733 -16z"/>
                <path d="M4 20l6.768 -6.768m2.46 -2.46l6.772 -6.772"/>
              </svg>
            </a>
          )}
        </div>
      </div>

      <p className="text-gray-200 mt-4">
        {subnetInfo?.description || 'No description available.'}
      </p>

      <SubnetMindshareView
        mindshareData={mindshareData}
        mindshareHistory={mindshareHistory}
        mindsharePercentChange={mindsharePercentChange}
        loadingMindshare={loadingMindshare}
        loadingHistory={loadingHistory}
        subnetData={subnetData}
        taoPrice={taoPrice}
        netuid={netuid}
      />
    </div>
  );
}